import { GENERATOR_POLL_INTERVAL_MS, QUEUE_SIZE_MULTIPLIER } from "@client-shared/lib/constants";
import { executeInGlobalPdfLibQueue, serializedCopyPages, serializedPdfCreate } from "@client-shared/lib/pdfPageFitter";
import { PDFDocument } from "pdf-lib";
import { fetchPdfArrayBuffer } from "./pdfFetchUtils";

// Simple utility to merge PDFs using pdf-lib
// This is only used for combining existing PDFs, not for generation
/**
 * Creates a serialized PDFDocument.load function that queues load operations to prevent race conditions.
 * Serializes all PDFDocument.load calls through the global queue to prevent pdf-lib state corruption.
 *
 * @returns A function that queues and executes PDFDocument.load operations sequentially
 */
const createSerializedPdfLoad = (): ((arrayBuffer: ArrayBuffer) => Promise<PDFDocument>) => {
  return async (arrayBuffer: ArrayBuffer): Promise<PDFDocument> => {
    return await executeInGlobalPdfLibQueue(async () => {
      // Clone ArrayBuffer to ensure isolation
      const clonedBuffer = arrayBuffer.slice(0);
      const loadedDocument: unknown = await PDFDocument.load(clonedBuffer);

      // Validate the result
      // Check for null first, then object type, then property access
      if (
        loadedDocument === null ||
        typeof loadedDocument !== "object" ||
        typeof (loadedDocument as { getPages?: unknown }).getPages !== "function"
      ) {
        throw new TypeError(`PDFDocument.load returned invalid document: ${typeof loadedDocument}`);
      }

      return loadedDocument as PDFDocument;
    });
  };
};

// Create a single shared serialized PDF loader instance
// This ensures all PDFDocument.load calls are serialized globally
export const serializedPdfLoad = createSerializedPdfLoad();

export class SimplePdfMerger {
  /**
   * Merges multiple PDFs into a single PDF.
   * Processes PDFs sequentially to avoid accumulating all PDFs in memory simultaneously.
   * This is critical for large datasets (e.g., 2000+ orders) to prevent memory issues.
   *
   * @param pdfUrls - Array of blob URLs or URLs to PDFs to merge
   * @param signal - Optional AbortSignal to cancel the operation
   * @returns Blob URL of the merged PDF
   */
  static async mergePdfs(pdfUrls: string[], signal?: AbortSignal): Promise<string> {
    try {
      if (pdfUrls.length === 0) {
        // Return empty PDF if no URLs provided
        const emptyPdf = await serializedPdfCreate();
        // Apply compression even for empty PDFs for consistency
        const pdfBytes = await emptyPdf.save({ useObjectStreams: true });
        const blob = new Blob([new Uint8Array(pdfBytes)], {
          type: "application/pdf",
        });
        return URL.createObjectURL(blob);
      }

      // Check if aborted before starting
      if (signal?.aborted) {
        throw new Error("Operation aborted");
      }

      // Create a new PDF document
      const mergedPdf = await serializedPdfCreate();

      // Process PDFs sequentially to avoid loading all into memory at once
      // This prevents memory accumulation for large datasets
      for (const url of pdfUrls) {
        // Check if aborted before each iteration
        if (signal?.aborted) {
          throw new Error("Operation aborted");
        }

        // Fetch and load one PDF at a time (validate PDF + avoid cached 403s for presigned URLs)
        // PDFs are loaded one at a time to bound browser memory.
        const arrayBuffer = await fetchPdfArrayBuffer(url, { signal }); // NOSONAR
        // PDFs are loaded one at a time to bound browser memory.
        const pdf = await serializedPdfLoad(arrayBuffer); // NOSONAR

        // Copy pages from this PDF to the merged PDF
        const pageIndices = pdf.getPageIndices();
        // Pages are copied into the merged document in order.
        const pages = await serializedCopyPages(mergedPdf, pdf, pageIndices); // NOSONAR
        for (const page of pages) {
          mergedPdf.addPage(page);
        }

        // Variables go out of scope here, allowing garbage collection between iterations
        // This prevents accumulation of PDF data in memory for large datasets
      }

      // Check if aborted before final save
      if (signal?.aborted) {
        throw new Error("Operation aborted");
      }

      // Generate the merged PDF with compression for file size reduction
      // Compression is only applied at final merge to minimize performance impact
      const pdfBytes = await mergedPdf.save({ useObjectStreams: true });
      const blob = new Blob([new Uint8Array(pdfBytes)], {
        type: "application/pdf",
      });

      return URL.createObjectURL(blob);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error("Operation aborted");
      }
      console.error("Error merging PDFs:", error);
      throw new Error("Failed to merge PDFs");
    }
  }

  /**
   * Adds all pages from a source PDF to the merged PDF document.
   *
   * @param mergedPdf - The target PDF document to add pages to.
   * @param pdf - The source PDF document to copy pages from.
   */
  private static async addPdfPagesToMerged(mergedPdf: PDFDocument, pdf: PDFDocument): Promise<void> {
    try {
      const pageIndices = pdf.getPageIndices();
      const pages = await serializedCopyPages(mergedPdf, pdf, pageIndices);
      for (const page of pages) {
        mergedPdf.addPage(page);
      }
    } catch (error) {
      console.error("Error processing PDF:", error);
      // Continue processing other PDFs even if one fails
    }
  }

  /**
   * Fetches a PDF from a URL and adds its pages to the merged document.
   * Handles retries and errors gracefully, skipping failed PDFs.
   *
   * @param url - The URL of the PDF to fetch and merge.
   * @param mergedPdf - The target PDF document to add pages to.
   * @param fetchTimeout - Timeout in milliseconds for each fetch attempt.
   * @param maxRetries - Maximum number of retry attempts for failed fetches.
   */
  private static async processPdfForMerge(url: string, mergedPdf: PDFDocument, fetchTimeout: number, maxRetries: number): Promise<void> {
    const pdf = await SimplePdfMerger.fetchPdfWithRetry(url, {
      timeout: fetchTimeout,
      maxRetries: maxRetries,
    });
    if (!pdf) {
      return; // Skip failed PDFs
    }
    await SimplePdfMerger.addPdfPagesToMerged(mergedPdf, pdf);
  }

  /**
   * Fills the URL queue from the generator up to the queue size limit.
   *
   * @param pdfUrlGenerator - Async generator that yields PDF URLs
   * @param urlQueue - Queue to fill with URLs from the generator
   * @param fetchConcurrency - Maximum number of concurrent fetches (used to calculate queue size)
   * @returns Promise resolving to true if generator is done, false otherwise
   */
  private static async fillUrlQueue(
    pdfUrlGenerator: AsyncGenerator<string, void, unknown>,
    urlQueue: string[],
    fetchConcurrency: number
  ): Promise<boolean> {
    while (urlQueue.length < fetchConcurrency * QUEUE_SIZE_MULTIPLIER) {
      const result = await pdfUrlGenerator.next();
      if (result.done) {
        return true;
      }
      urlQueue.push(result.value);
    }
    return false;
  }

  /**
   * Starts new PDF fetches up to the concurrency limit.
   * Processes URLs from the queue and adds them to activeFetches for tracking.
   *
   * @param urlQueue - Queue of URLs to process
   * @param activeFetches - Set of active fetch promises (modified in place)
   * @param fetchConcurrency - Maximum number of concurrent fetches
   * @param mergedPdf - PDF document to merge fetched pages into
   * @param fetchTimeout - Timeout in milliseconds for each fetch
   * @param maxRetries - Maximum number of retries for failed fetches
   */
  private static startNewFetches(
    urlQueue: string[],
    activeFetches: Set<Promise<void>>,
    fetchConcurrency: number,
    mergedPdf: PDFDocument,
    fetchTimeout: number,
    maxRetries: number
  ): void {
    while (activeFetches.size < fetchConcurrency && urlQueue.length > 0) {
      const url = urlQueue.shift();
      if (!url) {
        continue;
      }

      const fetchPromise = SimplePdfMerger.processPdfForMerge(url, mergedPdf, fetchTimeout, maxRetries).finally(() => {
        activeFetches.delete(fetchPromise);
      });
      activeFetches.add(fetchPromise);
    }
  }

  /**
   * Incrementally merges PDFs as they become available from an async generator.
   * This allows fetching and merging PDFs in parallel, preventing URL expiration
   * and timeout issues with large batches.
   *
   * @param pdfUrlGenerator - Async generator that yields PDF URLs as they become available
   * @param options - Configuration options
   * @param options.fetchConcurrency - Maximum number of PDFs to fetch in parallel (default: 5)
   * @param options.fetchTimeout - Timeout in milliseconds for each PDF fetch (default: 30000)
   * @param options.maxRetries - Maximum number of retries for failed fetches (default: 2)
   * @returns Blob URL of the merged PDF
   * @throws {Error} If PDF creation or merging fails due to a critical error
   *
   * @remarks
   * - Failed PDF fetches are logged and skipped; the merge continues with successfully fetched PDFs.
   * - If all PDF fetches fail, an empty PDF blob URL is returned.
   * - The function only throws if a critical error occurs during PDF creation or merging; otherwise, partial results are returned.
   * - The merged PDF is saved with `useObjectStreams: true`, which compresses the output for smaller file size and improved compatibility.
   */
  static async mergePdfsIncremental(
    pdfUrlGenerator: AsyncGenerator<string, void, unknown>,
    options?: {
      fetchConcurrency?: number;
      fetchTimeout?: number;
      maxRetries?: number;
    }
  ): Promise<string> {
    const fetchConcurrency = options?.fetchConcurrency ?? 5;
    const fetchTimeout = options?.fetchTimeout ?? 30000;
    const maxRetries = options?.maxRetries ?? 2;

    try {
      const mergedPdf = await serializedPdfCreate();
      const urlQueue: string[] = [];
      let isGeneratorDone = false;
      const activeFetches = new Set<Promise<void>>();

      // Start processing URLs as they become available
      while (!isGeneratorDone || urlQueue.length > 0 || activeFetches.size > 0) {
        // Fill the queue from the generator
        if (!isGeneratorDone) {
          // The generator is consumed in order as URLs become available.
          isGeneratorDone = await SimplePdfMerger.fillUrlQueue(pdfUrlGenerator, urlQueue, fetchConcurrency); // NOSONAR
        }

        // Start new fetches up to concurrency limit
        SimplePdfMerger.startNewFetches(urlQueue, activeFetches, fetchConcurrency, mergedPdf, fetchTimeout, maxRetries);

        // Wait for at least one fetch to complete before continuing
        if (activeFetches.size > 0) {
          // Wait for a fetch slot to free up before starting more (bounded concurrency).
          await Promise.race(activeFetches); // NOSONAR
        } else if (!isGeneratorDone) {
          // If no active fetches and generator not done, wait a bit for more URLs
          // Polling delay between checks for new generator URLs.
          await new Promise((resolve) => setTimeout(resolve, GENERATOR_POLL_INTERVAL_MS)); // NOSONAR
        }
      }

      // Wait for all remaining fetches to complete
      await Promise.all(activeFetches);

      // Generate the merged PDF with compression
      const pdfBytes = await mergedPdf.save({ useObjectStreams: true });
      const blob = new Blob([new Uint8Array(pdfBytes)], {
        type: "application/pdf",
      });

      return URL.createObjectURL(blob);
    } catch (error) {
      console.error("Error merging PDFs incrementally:", error);
      throw new Error(`Failed to merge PDFs incrementally: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Sets up an abort handler that links an external AbortSignal to an AbortController.
   * When the external signal aborts, the controller will also abort.
   *
   * @param externalSignal - Optional external AbortSignal to listen to.
   * @param controller - The AbortController to abort when external signal fires.
   * @returns The abort handler function if one was created, undefined otherwise.
   */
  private static setupAbortHandler(externalSignal: AbortSignal | undefined, controller: AbortController): (() => void) | undefined {
    if (!externalSignal) {
      return undefined;
    }
    const abortHandler = () => controller.abort();
    externalSignal.addEventListener("abort", abortHandler);
    return abortHandler;
  }

  /**
   * Cleans up the abort handler by removing the event listener.
   * Prevents memory leaks when called repeatedly.
   *
   * @param externalSignal - The external AbortSignal that was listened to.
   * @param abortHandler - The abort handler function to remove.
   */
  private static cleanupAbortHandler(externalSignal: AbortSignal | undefined, abortHandler: (() => void) | undefined): void {
    if (externalSignal && abortHandler) {
      externalSignal.removeEventListener("abort", abortHandler);
    }
  }

  /**
   * Validates that a fetched ArrayBuffer contains PDF data.
   *
   * @param arrayBuffer - The ArrayBuffer to validate.
   * @param url - The URL the PDF was fetched from.
   * @throws Error when the ArrayBuffer is empty or invalid.
   */
  private static validatePdfArrayBuffer(arrayBuffer: ArrayBuffer, url: string): void {
    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      throw new Error(`Invalid PDF data from ${url}: empty or null arrayBuffer`);
    }
  }

  /**
   * Asserts that a value is a valid PDFDocument instance.
   *
   * @param pdfDoc - The value to validate.
   * @param errorPrefix - Error message prefix for invalid documents.
   * @throws TypeError when the value is not a valid PDFDocument.
   */
  private static assertPdfDocumentInstance(pdfDoc: unknown, errorPrefix: string): asserts pdfDoc is PDFDocument {
    if (pdfDoc === null || typeof pdfDoc !== "object" || typeof (pdfDoc as { getPages?: unknown }).getPages !== "function") {
      throw new TypeError(`${errorPrefix}: ${typeof pdfDoc}`);
    }
  }

  /**
   * Loads a PDFDocument from an ArrayBuffer and performs immediate validation.
   *
   * @param arrayBuffer - The PDF data to load.
   * @param url - The URL the PDF was fetched from.
   * @returns Promise resolving to a validated PDFDocument.
   * @throws Error if loading or immediate validation fails.
   */
  private static async loadPdfDocument(arrayBuffer: ArrayBuffer, url: string): Promise<PDFDocument> {
    try {
      const pdfDoc = await serializedPdfLoad(arrayBuffer);
      this.assertPdfDocumentInstance(pdfDoc, "PDFDocument.load returned invalid document immediately after load");
      const testPages = pdfDoc.getPages();
      if (!Array.isArray(testPages) || testPages.length === 0) {
        throw new Error("PDFDocument.load returned document with no pages");
      }
      return pdfDoc;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      // Enhanced logging for NaN errors to help diagnose the issue
      if (errorMsg.includes("NaN") || errorMsg.includes("type NaN")) {
        console.error(`PDFDocument.load NaN error for ${url}:`, {
          arrayBufferSize: arrayBuffer.byteLength,
          errorMessage: errorMsg,
        });
      }
      throw new Error(`Failed to load PDF from ${url}: ${errorMsg}`);
    }
  }

  /**
   * Validates that a PDFDocument has accessible pages and sane dimensions.
   *
   * @param pdfDoc - The PDFDocument to validate.
   * @param url - The URL the PDF was fetched from.
   * @throws Error when the PDFDocument appears corrupted.
   */
  private static validatePdfPages(pdfDoc: PDFDocument, url: string): void {
    try {
      const pages = pdfDoc.getPages();
      if (!Array.isArray(pages) || pages.length === 0) {
        throw new Error(`PDF from ${url} has no pages`);
      }
      // Validate first page has valid dimensions
      const firstPage = pages[0];
      if (firstPage) {
        const width = firstPage.getWidth();
        const height = firstPage.getHeight();
        if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
          console.warn(`PDF from ${url} has corrupted dimensions (width=${width}, height=${height})`);
          // Don't throw here - let the recovery logic handle it
        }
      }
    } catch (error) {
      // If we can't get pages, the PDF is definitely corrupted
      throw new Error(`PDF from ${url} appears corrupted: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Fetches a PDF from a URL and loads it as a PDFDocument.
   *
   * @param url - The URL of the PDF to fetch.
   * @param signal - AbortSignal to cancel the request.
   * @returns Promise resolving to the loaded PDFDocument.
   * @throws Error if the response is not ok or fetch fails.
   */
  private static async fetchAndLoadPdf(url: string, signal: AbortSignal): Promise<PDFDocument> {
    // Use fetchPdfArrayBuffer from pdfFetchUtils (from upstream branch)
    const arrayBuffer = await fetchPdfArrayBuffer(url, { signal });
    this.validatePdfArrayBuffer(arrayBuffer, url);

    // Use serialized load to prevent race conditions
    // pdf-lib's PDFDocument.load may not be thread-safe for concurrent calls
    // serializedPdfLoad handles ArrayBuffer cloning internally to ensure isolation
    const pdfDoc = await this.loadPdfDocument(arrayBuffer, url);

    // Validate the loaded PDFDocument is valid
    // Check for null/undefined first, then object type, then property access
    // Note: typeof null === "object" in JavaScript, so !pdfDoc handles null before the typeof check
    this.assertPdfDocumentInstance(pdfDoc, `PDFDocument.load returned invalid document from ${url}`);

    // Quick validation: try to get pages to catch corrupted PDFs early
    this.validatePdfPages(pdfDoc, url);

    return pdfDoc;
  }

  /**
   * Handles fetch errors, converting AbortErrors to appropriate responses.
   * Returns null if aborted by external signal, throws timeout error if aborted by timeout.
   *
   * @param error - The error that occurred during fetch.
   * @param externalSignal - Optional external AbortSignal to check if externally aborted.
   * @param timeout - The timeout value in milliseconds for error message.
   * @returns null if externally aborted.
   * @throws Error with timeout message if aborted by timeout, or re-throws original error.
   */
  private static handleFetchAbortError(error: unknown, externalSignal: AbortSignal | undefined, timeout: number): null {
    if (error instanceof Error && error.name === "AbortError") {
      if (externalSignal?.aborted) {
        return null;
      }
      throw new Error(`Timeout after ${timeout}ms`);
    }
    throw error;
  }

  /**
   * Fetches a PDF with timeout and retry logic.
   * Shared utility to avoid code duplication.
   *
   * @param url - The URL of the PDF to fetch.
   * @param options - Optional configuration for timeout and retry behavior.
   * @param options.timeout - Timeout in milliseconds for each fetch attempt (default: 30000).
   * @param options.maxRetries - Maximum number of retry attempts for failed fetches (default: 2).
   * @param options.retryCount - Current retry attempt number (used internally for recursion).
   * @param options.signal - Optional AbortSignal to cancel the request.
   * @returns Promise resolving to the loaded PDFDocument, or null if all retries are exhausted.
   */
  static async fetchPdfWithRetry(
    url: string,
    options?: {
      timeout?: number;
      maxRetries?: number;
      retryCount?: number;
      signal?: AbortSignal;
    }
  ): Promise<PDFDocument | null> {
    const timeout = options?.timeout ?? 30000;
    const maxRetries = options?.maxRetries ?? 2;
    const retryCount = options?.retryCount ?? 0;
    const externalSignal = options?.signal;

    if (externalSignal?.aborted) {
      return null;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      const abortHandler = this.setupAbortHandler(externalSignal, controller);

      try {
        return await this.fetchAndLoadPdf(url, controller.signal);
      } catch (fetchError) {
        return this.handleFetchAbortError(fetchError, externalSignal, timeout);
      } finally {
        clearTimeout(timeoutId);
        this.cleanupAbortHandler(externalSignal, abortHandler);
      }
    } catch (error) {
      if (externalSignal?.aborted) {
        return null;
      }
      if (retryCount < maxRetries) {
        console.warn(`Failed to fetch PDF (attempt ${retryCount + 1}/${maxRetries + 1}):`, error);
        return this.fetchPdfWithRetry(url, {
          ...options,
          retryCount: retryCount + 1,
        });
      }
      console.error(`Failed to fetch PDF after ${maxRetries + 1} attempts:`, error);
      return null;
    }
  }
}
